<template>
  <div>
    <div
      v-click-tracker="analyticsParams"
      v-view-tracker="analyticsParams"
      class="card-info"
      @click="goToExp()"
    >
      <Img
        :src="sight.cover_photo?.medium"
        :alt="sight.name"
        aspect-ratio="4 / 3"
        border-radius="12px"
      />
      <h2
        v-if="sight?.name"
        class="title"
      >
        {{ sight.name }}
      </h2>
      <div
        v-if="sight.address"
        class="address"
      >
        {{ sight.address }}
      </div>
      <div
        v-if="sight.description"
        ref="more"
        :class="['subtitle', { 'subtitle--full': isPrerender }]"
      >
        {{ sight.description }}
      </div>
    </div>
    <div class="btns">
      <UiButton
        v-if="sight.excursions_count"
        :variant="UiButtonVariant.PRIMARY"
        :color-mode="UiButtonColorMode.NEUTRAL"
        is-full-width
        class="btn"
        :text="btnTitle"
        :href="goToExpLink"
        target="_blank"
        data-test="listing-around-title"
        @clicked="trackExpClick"
      />

      <UiButton
        :variant="UiButtonVariant.SECONDARY"
        :color-mode="UiButtonColorMode.NEUTRAL"
        class="btn"
        is-full-width
        text="Подробнее"
        data-test="listing-around-title"
        :href="sightLink"
        @click.prevent="useSight"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { UiButton, UiButtonVariant, UiButtonColorMode } from '@experience/core-button-ui';
  import { getBootstrapData } from '@experience/core-hostapp-share-lib';
  import { computed } from 'vue';
  import { useRoute } from 'vue-router';

  import { trackEvent, trackEventImmediately, useClickTracker, useViewTracker } from '@/services/analytics';
  import { type CityTagsAdvanced } from '@/shared/models/types';
  import Img from '@/shared/ui/ResponsiveImage.vue';
  import { useSightAnalyticsParams } from '@/shared/utils/analytics';
  import { pluralize } from '@/shared/utils/language';

  const getBootstrapDataSafe = getBootstrapData as () => ReturnType<typeof getBootstrapData> | undefined;
  const isPrerender = computed(() => getBootstrapDataSafe()?.prerenderMode);

  const vViewTracker = useViewTracker('attractions:sights-snippet.view');
  // @ts-expect-error события нет в схеме Sogu
  const vClickTracker = useClickTracker('attractions:sights:sight-snippet.click');

  const route = useRoute();

  const { slug } = route.params;

  const props = defineProps<{
    sight: CityTagsAdvanced;
  }>();

  const analyticsParams = useSightAnalyticsParams(() => props.sight);

  const routeSlug = computed(() => (Array.isArray(slug) ? (slug[0] ?? '') : (slug ?? '')));
  const sightLink = computed(() => `/sights/${String(props.sight.id)}-${props.sight.slug ?? ''}/`);
  const goToExpLink = computed(
    () => `/experience/${routeSlug.value}/${String(props.sight.id)}-${props.sight.slug ?? ''}/`,
  );

  const goToExp = (withAnalytics = false) => {
    if (withAnalytics) {
      void trackEvent('attractions:sights:sight-snippet:exp-count.click', analyticsParams.value);
    }

    window.open(goToExpLink.value, '_blank');
  };

  const trackExpClick = () => {
    void trackEvent('attractions:sights:sight-snippet:exp-count.click', analyticsParams.value);
  };

  const btnTitle = computed(() =>
    props.sight.excursions_count
      ? `${String(props.sight.excursions_count)} ${pluralize(props.sight.excursions_count, ['экскурсия', 'экскурсии', 'экскурсий'])}`
      : '',
  );

  const useSight = async () => {
    try {
      await trackEventImmediately('attractions:sights:sight-card:more-details.click', analyticsParams.value);
    } finally {
      window.location.href = sightLink.value;
    }
  };
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as legacy-typography;
  @use '@experience/shared/dist/assets/adaptivity' as legacy-adaptivity;

  .card-info {
    cursor: pointer;
  }

  .title {
    margin-top: var(--alias-space-12);

    @include legacy-typography.base-600-500-extra;
  }

  .address {
    @include legacy-typography.base-400-400-regular;
  }

  .subtitle {
    position: relative;
    display: -webkit-box;
    margin-top: var(--alias-space-8);
    overflow: hidden;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;

    &--full {
      display: block;
      overflow: visible;
      -webkit-line-clamp: unset;
    }

    @include legacy-typography.base-400-400-regular;
  }

  .btn {
    width: 100%;
    margin-top: var(--alias-space-12);
  }

  .mirror {
    transform: scaleX(-1);
  }

  :deep(.btn-next) {
    transform: translate(0%, -50%);
  }

  :deep(.btn-prev) {
    transform: translate(0%, -50%);
  }

  .slice {
    position: relative;
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
  }

  .btns {
    display: flex;
    gap: 8px;
  }
</style>
